import { LoadingButton } from "@mui/lab";
import { Box, Button, FormControl, Grid, InputLabel, MenuItem, Select, Stack, Switch, Typography } from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import CookieJs from "js-cookie";
import moment from "moment";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useMutation } from "react-query";
import { Link as RouterLink, useLocation } from "react-router-dom";
import Breadcrumb from "../components/Breadcrumb";
import MainLayout from "../components/MainLayout";
import ProtectedPermissionPage from "../components/ProtectedPermissionPage";
import ProtectedPermissionView from "../components/ProtectedPermissionView";
import useNotification from "../hooks/useNotification";
import { default as event, default as service } from "../services/event";
import category from "../services/category";
import queryDefault from "../utils/queryDefault";
import Category from "./Category";

export default function EventDetail() {
  const user = JSON.parse(CookieJs.get("USER") || "");
  const location = useLocation();
  const path = location.pathname.split("/");
  const event_id = path.pop() || "";
  const [checkoutStatus, setCheckoutStatus] = useState(true);
  const [redeemReport, setRedeemReport] = useState([
    { username: "", count: "0" },
  ]);
  const [checkinReport, setCheckinReport] = useState([
    { username: "", count: "0" },
  ]);
  const [redeemStats, setRedeemStats] = useState([
    { grouped_hours: "", count: "0" },
  ]);
  const [checkinStats, setCheckinStats] = useState([
    { grouped_hours: "", count: "0" },
  ]);
  
  // Category-specific chart data
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [categoryRedeemStats, setCategoryRedeemStats] = useState([
    { username: "", count: "0" },
  ]);
  const [categoryCheckinStats, setCategoryCheckinStats] = useState([
    { username: "", count: "0" },
  ]);
  
  const chartSetting = {
    height: 300,
  };
  const [data, setData] = useState({
    id: "",
    name: "",
    redeem_total: 0,
    checkin_total: 0,
    first_checkin_total: 0,
    total_ticket: 0,
    additional_wristband: 0,
    created_time: null,
    created_by: "",
    admin_name: "",
    phone_number: "",
    date_from: null,
    date_to: null,
    file_path: "",
    is_custom_wristband: false,
  });

  useEffect(() => {
    service.viewEventDetail(event_id).then((response) => {
      setData({
        id: event_id,
        redeem_total: response.data.is_masked_total ? response.data.masked_redeem : response.data.redeem_total,
        checkin_total: response.data.is_masked_total ? response.data.masked_check_in : response.data.checkin_total,
        first_checkin_total: response.data.is_masked_total ? response.data.masked_first_check_in : response.data.first_checkin_total,
        total_ticket: response.data.is_masked_total ? response.data.masked_ticket : response.data.total_ticket,
        additional_wristband: response.data.additional_wristband,
        name: response.data.name,
        created_time: response.data.created_time,
        created_by: response.data.created_by,
        admin_name: response.data.admin_name,
        phone_number: response.data.phone_number,
        date_from: response.data.date_from,
        date_to: response.data.date_to,
        file_path: response.data.file_path,
        is_custom_wristband: response.data.is_custom_wristband,
      });
      if (response.data.redeem_stats?.length > 0) {
        for (const i of response.data.redeem_stats) {
          var date = new Date(i.grouped_hours);
          i.grouped_hours = `${date.getDate()}-${date.getHours()}`;
          i.count = Number(i.count);
        }
      }
      if (response.data?.checkin_stats?.length > 0) {
        for (const i of response.data.checkin_stats) {
          var checkinDate = new Date(i.grouped_hours);
          i.grouped_hours = `${checkinDate.getDate()}-${checkinDate.getHours()}`;
          i.count = Number(i.count);
        }
      }
      if (response.data.redeem_stats.length > 0) {
        for (const i of response.data.redeem_stats) {
          i.count = Number(i.count);
        }
        setRedeemStats(response.data.redeem_stats);
      }
      if (response.data.checkin_stats.length > 0) {
        for (const i of response.data.checkin_stats) {
          i.count = Number(i.count);
        }
        setCheckinStats(response.data.checkin_stats);
      }
      setCheckoutStatus(response.data.ischeckout);
    });
    service
      .getReport({ reportType: "redeem", eventId: event_id })
      .then((response) => {
        if (response?.data.length > 0) {
          for (const i of response.data) {
            i.count = Number(i.count);
          }
          setRedeemReport(response.data);
        }
      });
    service
      .getReport({ reportType: "checkin", eventId: event_id })
      .then((response) => {
        if (response?.data.length > 0) {
          for (const i of response.data) {
            i.count = Number(i.count);
          }
          setCheckinReport(response.data);
        }
      });
  }, [checkoutStatus, event_id]);

  // Fetch categories for dropdown
  useEffect(() => {
    category.listEventCategories(event_id).then((response) => {
      if (response?.data?.length > 0) {
        setCategories(response.data);
        // Set first category as default
        setSelectedCategory(response.data[0].id);
      }
    });
  }, [event_id]);

  // Fetch category-specific chart data when category is selected
  useEffect(() => {
    if (selectedCategory && event_id) {
      service.getCategoryChartData(parseInt(event_id), parseInt(selectedCategory))
        .then((response) => {
          if (response.data.redeem_stats?.length > 0) {
            const processedRedeemStats = response.data.redeem_stats.map((item: any) => {
              return {
                ...item,
                count: Number(item.count)
              };
            });
            setCategoryRedeemStats(processedRedeemStats);
          } else {
            setCategoryRedeemStats([{ username: "", count: "0" }]);
          }
          
          if (response.data.checkin_stats?.length > 0) {
            const processedCheckinStats = response.data.checkin_stats.map((item: any) => {
              return {
                ...item,
                count: Number(item.count)
              };
            });
            setCategoryCheckinStats(processedCheckinStats);
          } else {
            setCategoryCheckinStats([{ username: "", count: "0" }]);
          }
        })
        .catch((error) => {
          console.error('Error fetching category chart data:', error);
          setCategoryRedeemStats([{ username: "", count: "0" }]);
          setCategoryCheckinStats([{ username: "", count: "0" }]);
        });
    }
  }, [selectedCategory, event_id]);

  const handleChange = async () => {
    service.changeCheckoutStatus(event_id).then((response) => {
      setCheckoutStatus(response.data);
    });
  };

  const handleCategoryChange = (event: any) => {
    setSelectedCategory(event.target.value);
  };
  const notification = useNotification();
  
  const uploadVolunteer = useMutation("upload_volunteer", service.uploadVolunteer, {
    onSuccess: () => {
      notification.onOpen({
        message: "Volunteer was successfully uploaded!",
        type: "success",
        position: "top",
      });
      window.location.reload();
    },
    onError: (error: any) => {
      notification.onOpen({
        message: error?.response?.data?.message,
        type: "error",
        position: "top",
      });
    },
    ...queryDefault,
  });

  const handleFileChangeVolunteer = (event: React.ChangeEvent<HTMLInputElement>) => {
    const formData = new FormData();
    formData.append("event_id", event_id);
    event.target.files && formData.append("file", event.target.files[0]);
    formData.append("username", user.username);
    uploadVolunteer.mutate(formData);
  };
  const download = useMutation("download", event.downloadFile);

  const handleDownload = () => download.mutate(data.file_path);

  return (
    <MainLayout>
      <Helmet>
        <title>Event</title>
      </Helmet>

      <Breadcrumb
        breadcrumbs={[{ label: "Event", href: "/app/event" }]}
        label={data.name}
        href={user.is_admin ? "/app/event" : ""}
      />

      <ProtectedPermissionPage acceptPermissions={[1, 7]}>
        <Box sx={{ mt: 3 }}>
          <Grid container spacing={{ lg: 4, md: 2 }}>
            <Grid item lg={4} md={12}>
              <Stack sx={{ mt: 2 }}>
                <Typography variant="body1" color="grey.600" fontWeight="300">
                  Created Time
                </Typography>
                <Typography
                  variant="body1"
                  color="grey.600"
                  fontWeight="700"
                  sx={{ mt: 0.3 }}
                >
                  {moment(data.created_time).format("D/MM/YYYY HH:mm")}
                </Typography>
              </Stack>

              <Stack sx={{ mt: 2 }}>
                <Typography variant="body1" color="grey.600" fontWeight="300">
                  Created By
                </Typography>
                <Typography
                  variant="body1"
                  color="grey.600"
                  fontWeight="700"
                  sx={{ mt: 0.3 }}
                >
                  {data.created_by}
                </Typography>
              </Stack>

              <Stack sx={{ mt: 2 }}>
                <Typography variant="body1" color="grey.600" fontWeight="300">
                  Event Start Date
                </Typography>
                <Typography
                  variant="body1"
                  color="grey.600"
                  fontWeight="700"
                  sx={{ mt: 0.3 }}
                >
                  {moment(data.date_from).format("D/MM/YYYY")}
                </Typography>
              </Stack>

              <Stack sx={{ mt: 2 }}>
                <Stack>
                  <Typography variant="body1" color="grey.600" fontWeight="300">
                    Event End Date
                  </Typography>
                  <Typography
                    variant="body1"
                    color="grey.600"
                    fontWeight="700"
                    sx={{ mt: 0.3 }}
                  >
                    {moment(data.date_to).format("D/MM/YYYY")}
                  </Typography>
                </Stack>
              </Stack>
            </Grid>

            <Grid item lg={4} md={12}>
              <Stack sx={{ mt: 2 }}>
                <Typography variant="body1" color="grey.600" fontWeight="300">
                  Total Ticket
                </Typography>
                <Typography
                  variant="body1"
                  color="green"
                  fontWeight="700"
                  sx={{ mt: 0.3 }}
                >
                  {data.total_ticket}
                </Typography>
              </Stack>

              <Stack sx={{ mt: 2 }}>
                <Typography variant="body1" color="grey.600" fontWeight="300">
                  Total First Check In
                </Typography>
                <Typography
                  variant="body1"
                  color="green"
                  fontWeight="700"
                  sx={{ mt: 0.3 }}
                >
                  {data.first_checkin_total}
                </Typography>
              </Stack>

              <Stack sx={{ mt: 2 }}>
                <Typography variant="body1" color="grey.600" fontWeight="300">
                  Total Redeem
                </Typography>
                <Typography
                  variant="body1"
                  color="green"
                  fontWeight="700"
                  sx={{ mt: 0.3 }}
                >
                  {data.redeem_total}
                </Typography>
              </Stack>

              <Stack sx={{ mt: 2 }}>
                <Typography variant="body1" color="grey.600" fontWeight="300">
                  Total Check In
                </Typography>
                <Typography
                  variant="body1"
                  color="green"
                  fontWeight="700"
                  sx={{ mt: 0.3 }}
                >
                  {data.checkin_total}
                </Typography>
              </Stack>
            </Grid>

            <Grid item lg={4} md={12}>
              <Stack sx={{ mt: 2 }}>
                <Typography variant="body1" color="grey.600" fontWeight="300">
                  Total Additional Wristband
                </Typography>
                <Typography
                  variant="body1"
                  color="green"
                  fontWeight="700"
                  sx={{ mt: 0.3 }}
                >
                  {data.additional_wristband}
                </Typography>
              </Stack>
              <Grid container spacing={{ lg: 2, md: 1 }}>
                <Grid item lg={6} md={12}>
                  <Stack sx={{ mt: 2 }}>
                    <Typography
                      variant="body1"
                      color="grey.600"
                      fontWeight="300"
                    >
                      Enable Checkout
                    </Typography>
                    <Switch
                      checked={checkoutStatus}
                      onChange={handleChange}
                      inputProps={{ "aria-label": "controlled" }}
                    />
                  </Stack>
                </Grid>
                <Grid item lg={6} md={12}>
                  <Stack sx={{ mt: 2 }}>
                    <Typography
                      variant="body1"
                      color="grey.600"
                      fontWeight="300"
                    >
                      Custom Wristband
                    </Typography>
                    <Typography
                      variant="body1"
                      color={data.additional_wristband ? "green" : "red"}
                      fontWeight="700"
                      sx={{ mt: 0.3 }}
                    >
                      {data.is_custom_wristband ? "Yes" : "No"}
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>

              <Stack sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  component={RouterLink}
                  to={`/app/event/redeem-logs/${data.id}`}
                >
                  Redeem Logs
                </Button>
              </Stack>

              <Stack sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  component={RouterLink}
                  to={`/app/event/checkin-checkout-logs/${data.id}`}
                >
                  Checkin/Checkout Logs
                </Button>
              </Stack>
            </Grid>
          </Grid>

          <ProtectedPermissionView acceptPermissions={[1, 99]}>
            <Grid sx={{ mt: 2 }}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  component={RouterLink}
                  to={`/app/event/upload-management/${data.id}`}
                >
                  Manage File Uploads
                </Button>
                <Button variant="contained" component="label">
                  Upload Volunteer
                  <input onChange={handleFileChangeVolunteer} type="file" hidden />
                </Button>
              </Stack>
              <Stack direction="row" spacing={1} mt={2}>
                <Button
                  variant="outlined"
                  color="primary"
                  component={RouterLink}
                  to={`/TemplateVolunteerList.xlsx`}
                  target="_blank"
                >
                  Template Volunteer
                </Button>
                {data.file_path ? (
                  <Stack sx={{ mt: 2 }}>
                    <LoadingButton
                      variant="outlined"
                      color="primary"
                      onClick={() => handleDownload()}
                      loading={download.isLoading}
                    >
                      Download Latest Excel
                    </LoadingButton>
                  </Stack>
                ) : (
                  <></>
                )}
              </Stack>
            </Grid>
          </ProtectedPermissionView>
          <Category id={event_id} />
          <Grid marginTop="10px"></Grid>
          <Typography variant="h3">Redeem Graph</Typography>
          <BarChart
            dataset={redeemReport}
            xAxis={[{ scaleType: "band", dataKey: "username", label: "User" }]}
            series={[{ dataKey: "count", type: "bar" }]}
            {...chartSetting}
          />
          <Grid marginTop="10px"></Grid>
          <Typography variant="h3">Check In Graph</Typography>
          <BarChart
            dataset={checkinReport}
            xAxis={[
              {
                scaleType: "band",
                dataKey: "username",
                label: "User",
              },
            ]}
            series={[{ dataKey: "count", type: "bar" }]}
            {...chartSetting}
          />
          <Grid marginTop="10px"></Grid>
          <Typography variant="h3">Redeem Statistics</Typography>
          <BarChart
            dataset={redeemStats}
            xAxis={[
              {
                scaleType: "band",
                dataKey: "grouped_hours",
                label: "Date-Hour",
              },
            ]}
            series={[{ dataKey: "count", label: "Total" }]}
            {...chartSetting}
          />
          <Grid marginTop="10px"></Grid>
          <Typography variant="h3">Checkin Statistics</Typography>
          <BarChart
            dataset={checkinStats}
            xAxis={[
              {
                scaleType: "band",
                dataKey: "grouped_hours",
                label: "Date-Hour",
              },
            ]}
            series={[{ dataKey: "count", label: "Total" }]}
            {...chartSetting}
          />
          
          {/* Category-specific charts */}
          <Grid marginTop="20px"></Grid>
          <Typography variant="h3">Category-Specific Charts</Typography>
          
          {/* Category Dropdown */}
          <Box sx={{ mt: 2, mb: 3, minWidth: 200 }}>
            <FormControl fullWidth>
              <InputLabel id="category-select-label">Select Category</InputLabel>
              <Select
                labelId="category-select-label"
                id="category-select"
                value={selectedCategory}
                label="Select Category"
                onChange={handleCategoryChange}
              >
                {categories.map((cat: any) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          
          {/* Category-specific Redeem Chart */}
          <Grid marginTop="10px"></Grid>
          <Typography variant="h4">Category Redeem Statistics</Typography>
          <BarChart
            dataset={categoryRedeemStats}
            xAxis={[
              {
                scaleType: "band",
                dataKey: "username",
                label: "User",
              },
            ]}
            series={[{ dataKey: "count", label: "Total" }]}
            {...chartSetting}
          />
          
          {/* Category-specific Checkin Chart */}
          <Grid marginTop="10px"></Grid>
          <Typography variant="h4">Category Checkin Statistics</Typography>
          <BarChart
            dataset={categoryCheckinStats}
            xAxis={[
              {
                scaleType: "band",
                dataKey: "username",
                label: "User",
              },
            ]}
            series={[{ dataKey: "count", label: "Total" }]}
            {...chartSetting}
          />
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}
